import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: { 'Content-Type': 'application/json' },
});

// Auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('padsmash_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
};

// Photos
export const photoAPI = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getUrl: (photoId) => `${API_URL}/api/photos/${photoId}`,
};

// Listings
export const listingsAPI = {
  create: (data) => api.post('/listings', data),
  getAll: (params) => api.get('/listings', { params }),
  getOne: (id) => api.get(`/listings/${id}`),
  update: (id, data) => api.put(`/listings/${id}`, data),
  delete: (id) => api.delete(`/listings/${id}`),
  updatePhotos: (id, photoIds) => api.put(`/listings/${id}/photos`, photoIds),
  getMine: () => api.get('/my/listings'),
  confirmAvailability: (id) => api.post(`/listings/${id}/confirm-availability`),
};

// Orders
export const ordersAPI = {
  create: (data) => api.post('/orders', data),
  checkoutStatus: (sessionId) => api.get(`/orders/checkout-status/${sessionId}`),
  paymentStatus: (orderId) => api.get(`/orders/${orderId}/payment-status`),
  getMyOrders: () => api.get('/my/orders'),
  getMySales: () => api.get('/my/sales'),
  generateLabel: (orderId) => api.post(`/orders/${orderId}/shipping-label`),
  downloadLabel: (orderId) => api.get(`/orders/${orderId}/label-pdf`, { responseType: 'blob' }),
  confirmDelivery: (orderId) => api.post(`/orders/${orderId}/confirm-delivery`),
  rate: (orderId, data) => api.post(`/orders/${orderId}/rate`, data),
  reportDeliveryIssue: (orderId, reason) => api.post(`/orders/${orderId}/report-issue`, { reason }),
};

// Messages
export const messagesAPI = {
  createConversation: (listingId) => api.post('/conversations', { listing_id: listingId }),
  getConversations: () => api.get('/conversations'),
  getMessages: (convId, lang) => api.get(`/conversations/${convId}/messages`, { params: lang ? { lang } : {} }),
  sendMessage: (convId, content, language) => api.post(`/conversations/${convId}/messages`, { content, language: language || 'fr' }),
  getUnreadCount: () => api.get('/conversations/unread-count'),
};

// Market data
export const marketAPI = {
  getBrandsModels: () => api.get('/brands-models'),
  getPriceSuggestion: (params) => api.get('/price-suggestion', { params }),
  getModelSpecs: (params) => api.get('/model-specs', { params }),
};

// Sellers
export const sellersAPI = {
  getProfile: (id) => api.get(`/sellers/${id}`),
  getRatings: (id, params) => api.get(`/sellers/${id}/ratings`, { params }),
};

// Shipping
export const shippingAPI = {
  getRates: (data) => api.post('/shipping/rates', data),
  getServicePoints: (postalCode, carrier = 'mondial_relay') => 
    api.get('/service-points', { params: { postal_code: postalCode, carrier } }),
  getPacklinkDropoffs: (postalCode, serviceId, country = 'FR') =>
    api.get('/shipping/packlink-dropoffs', { params: { postal_code: postalCode, service_id: serviceId, country } }),
};

// Favorites
export const favoritesAPI = {
  add: (listingId) => api.post(`/favorites/${listingId}`),
  remove: (listingId) => api.delete(`/favorites/${listingId}`),
  getAll: () => api.get('/favorites'),
  getIds: () => api.get('/favorites/ids'),
};

// Notifications
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  readAll: () => api.post('/notifications/read-all'),
  read: (id) => api.post(`/notifications/${id}/read`),
};

// Offers (Négociation)
export const offersAPI = {
  create: (data) => api.post('/offers', data),
  getReceived: () => api.get('/offers/received'),
  getSent: () => api.get('/offers/sent'),
  accept: (offerId) => api.post(`/offers/${offerId}/accept`),
  decline: (offerId) => api.post(`/offers/${offerId}/decline`),
  counter: (offerId, amount) => api.post(`/offers/${offerId}/counter`, { amount }),
  acceptCounter: (offerId) => api.post(`/offers/${offerId}/accept-counter`),
  getAccepted: (listingId) => api.get(`/offers/accepted/${listingId}`),
};

// Wallet
export const walletAPI = {
  get: () => api.get('/wallet'),
  getBankDetails: () => api.get('/wallet/bank-details'),
  updateBankDetails: (data) => api.put('/wallet/bank-details', data),
  withdraw: (amount) => api.post('/wallet/withdraw', { amount }),
};

// Admin
export const adminAPI = {
  checkAdmin: () => api.get('/admin/check'),
  getStats: () => api.get('/admin/stats'),
  getEnhancedStats: () => api.get('/admin/enhanced-stats'),
  getChartData: (weeks) => api.get('/admin/chart-data', { params: { weeks } }),
  getMarketPrices: (params) => api.get('/market-prices', { params }),
  triggerScrape: () => api.post('/scrape-prices'),
  getScrapeStatus: () => api.get('/scrape-status'),
  getScrapeSchedule: () => api.get('/scrape-schedule'),
  updateScrapeSchedule: (data) => api.put('/scrape-schedule', data),
  promoteUser: (email) => api.post('/admin/promote', { email }),
  getKnownModels: () => api.get('/admin/known-models'),
  createManualPrice: (data) => api.post('/admin/market-prices', data),
  updateManualPrice: (brand, modelKey, data) => api.put(`/admin/market-prices/${brand}/${modelKey}`, data),
  deleteMarketPrice: (brand, modelKey) => api.delete(`/admin/market-prices/${brand}/${modelKey}`),
  getOrders: (params) => api.get('/admin/orders', { params }),
  getUsers: (params) => api.get('/admin/users', { params }),
  toggleBlockUser: (userId) => api.post(`/admin/users/${userId}/toggle-block`),
  updateOrderStatus: (orderId, status) => api.post(`/admin/orders/${orderId}/update-status?status=${status}`),
  getListings: (params) => api.get('/admin/listings', { params }),
  deleteListing: (listingId) => api.delete(`/admin/listings/${listingId}`),
  updateListingStatus: (listingId, status) => api.put(`/admin/listings/${listingId}/status?status=${status}`),
  approveListing: (listingId) => api.post(`/admin/listings/${listingId}/approve`),
  rejectListing: (listingId, reason) => api.post(`/admin/listings/${listingId}/reject`, { reason }),
  reassignListing: (listingId, email, sourceUrl) => api.post(`/admin/listings/${listingId}/reassign`, { email, source_url: sourceUrl }),
  getPendingListingsCount: () => api.get('/admin/pending-listings-count'),
  getReports: (params) => api.get('/admin/reports', { params }),
  getReportsPendingCount: () => api.get('/admin/reports/pending-count'),
  resolveReport: (reportId, action) => api.put(`/admin/reports/${reportId}/resolve?action=${action}`),
  // Delivery issues
  getDeliveryIssues: () => api.get('/admin/delivery-issues'),
  resolveDeliveryIssue: (issueId, data) => api.put(`/admin/delivery-issues/${issueId}/resolve`, data),
  // Promo codes
  getPromoCodes: () => api.get('/admin/promocodes'),
  createPromoCode: (data) => api.post('/admin/promocodes', data),
  updatePromoCode: (id, data) => api.put(`/admin/promocodes/${id}`, data),
  deletePromoCode: (id) => api.delete(`/admin/promocodes/${id}`),
  // Banners
  getBanners: () => api.get('/admin/banners'),
  createBanner: (data) => api.post('/admin/banners', data),
  updateBanner: (id, data) => api.put(`/admin/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/admin/banners/${id}`),
  // Withdrawals
  getWithdrawals: (status) => api.get('/admin/withdrawals', { params: { status } }),
  processWithdrawal: (id, data) => api.put(`/admin/withdrawals/${id}/process`, data),
  // Analytics
  getAnalytics: (source = 'all') => api.get(`/analytics/stats?source=${encodeURIComponent(source)}`),
};

// Promo codes (public)
export const promoAPI = {
  validate: (code, item_price) => api.post('/promocodes/validate', { code, item_price }),
};

// Banners (public)
export const bannersAPI = {
  getActive: () => api.get('/banners'),
};

// Reports
export const estimationAPI = {
  estimate: (data) => api.post('/estimate', data),
};

export const addressAPI = {
  search: (q) => api.get('/address/search', { params: { q, limit: 5 } }),
};

export const reportsAPI = {
  create: (data) => api.post('/reports', data),
};

// Purchase Requests (Vinted/Leboncoin style buying flow)
export const purchaseRequestsAPI = {
  create: (data) => api.post('/purchase-requests', data),
  confirm: (requestId) => api.post(`/purchase-requests/${requestId}/confirm`),
  decline: (requestId) => api.post(`/purchase-requests/${requestId}/decline`),
  cancel: (requestId) => api.delete(`/purchase-requests/${requestId}`),
  getForListing: (listingId) => api.get(`/purchase-requests/listing/${listingId}`),
  getPending: () => api.get('/purchase-requests/pending'),
};

export const blogAPI = {
  getArticles: (category, lang) => api.get('/blog/articles', { params: { ...(category ? { category } : {}), ...(lang ? { lang } : {}) } }),
  getArticle: (slug, lang) => api.get(`/blog/articles/${slug}`, { params: lang ? { lang } : {} }),
  getCategories: () => api.get('/blog/categories'),
  // Admin
  adminGetArticles: () => api.get('/blog/admin/articles'),
  adminGetArticle: (id) => api.get(`/blog/admin/articles/${id}`),
  adminCreateArticle: (data) => api.post('/blog/admin/articles', data),
  adminUpdateArticle: (id, data) => api.put(`/blog/admin/articles/${id}`, data),
  adminDeleteArticle: (id) => api.delete(`/blog/admin/articles/${id}`),
  adminTogglePublish: (id) => api.patch(`/blog/admin/articles/${id}/publish`),
};

export default api;
